// src/utils/taxCodes.js
// TAX CODES SEAM 1 VERIFIER: taxcodes-8d4f1a6e
//
// Web mirror of functions/utils/taxCodes.js for Settings › Taxes: the roll-out switch, the
// carry-over code, totals and labels, and the same validation with the same sentences, so the
// page can explain a problem before it calls saveTaxCodes. The SERVER decides: it re-checks
// everything, and firestore.rules make settings/taxCodes server-write-only. taxCodes.test.js
// runs the same inputs through both validators and requires the same answer.
//
// No Firebase imports here (pure), so it is testable without the app.

import { HIGH_LEVEL_ROLES } from './permissions';
import { LEGACY_TAX_CODES_ORG_IDS, taxCodesSwitchOn, savedTaxCodes } from './lineTax'; // taxlines-771ce983

// ─── roll-out switch ────────────────────────────────────────────────────────
// taxlines-771ce983: the switch is now `enabled` on organizations/{org}/settings/taxCodes, set only
// by functions/scripts/setTaxCodes.js; missing = the companies below (the test company). The rule
// is lineTax.taxCodesSwitchOn, shared with the server and the phone. isTaxCodesOn(orgId) with no
// doc answers the "missing" case, which is what the Settings tab still uses until the editor
// stages pass the doc in. The server checks the switch on every save.
export const TAX_CODES_FOR_EVERYONE = false;
export const TAX_CODES_ORG_IDS = Object.freeze([...LEGACY_TAX_CODES_ORG_IDS]); // Spatchy Test Company
export const isTaxCodesOn = (orgId, taxCodesDoc) =>
    TAX_CODES_FOR_EVERYONE || taxCodesSwitchOn(orgId, taxCodesDoc || null);
// The codes the page starts from: the saved ones, else the carry-over (also when the doc holds only the switch).
export const startingTaxCodes = (stored, org) => savedTaxCodes(stored) || carryOverCodes(org);

// Owner / Admin / Manager in every spelling + Office Staff (= the server's EDIT_ROLES).
export const TAX_EDIT_ROLES = Object.freeze(['Office Staff', ...HIGH_LEVEL_ROLES]);
export const canEditTaxes = (role) => TAX_EDIT_ROLES.includes(role);

export const TAX_LIMITS = Object.freeze({ codes: 50, parts: 10, name: 60 });

// ─── rates and labels ───────────────────────────────────────────────────────
const thousandths = (r) => Math.round(Number(r) * 1000);
const hasAtMost3Decimals = (r) => Math.abs(r * 1000 - Math.round(r * 1000)) < 1e-6;
export const codeTotal = (code) =>
    ((code && Array.isArray(code.parts)) ? code.parts : []).reduce((t, p) => t + thousandths(p.rate), 0) / 1000;

// 7.125 → "7.125", 5 → "5", 12.1 → "12.1".
export const formatRate = (r) => String(thousandths(r) / 1000);

// "GST 5% + PST 7% = 12%"; one part: "Tax 8.5%".
export const describeCode = (code) => {
    const parts = (code && Array.isArray(code.parts)) ? code.parts : [];
    const list = parts.map((p) => `${p.name} ${formatRate(p.rate)}%`).join(' + ');
    return parts.length > 1 ? `${list} = ${formatRate(codeTotal(code))}%` : list;
};

// A rate typed in a box → a number, or undefined when the box is empty / not a number.
export const parseRateInput = (text) => {
    const s = String(text ?? '').trim();
    if (s === '' || !/^-?\d*\.?\d*$/.test(s) || s === '.' || s === '-') return undefined;
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
};

// ─── carry-over (same ids, rate and default as the server's) ────────────────
// One difference on purpose: the PART is named plain "Tax", so the card reads "Tax 8.5%" and the
// name doesn't go stale when the rate is edited. The server's carryOverCodes names it "Tax 8.5%",
// but the server only uses its copy for ids (never deleted); the names stored are the ones the
// page sends.
export const CARRY_ID = 'carry';
export const CARRY_LABEL = 'Carried over from the tax rate you entered in Company settings.';
export function carryOverCodes(org) {
    const n = Number(org && org.taxRate);
    const rate = Number.isFinite(n) ? thousandths(n) / 1000 : 0;
    return [{
        id: CARRY_ID,
        name: 'Tax',
        parts: [{ id: `${CARRY_ID}-1`, name: 'Tax', rate }],
        isDefault: true,
        stopped: false,
        stoppedAt: null,
        stoppedBy: null,
        carriedOver: true,
    }];
}

// ─── validation (same sentences as the server) ──────────────────────────────
// Returns null when the list is fine, else { message, index } — index = the code at fault
// (null for list-wide problems like the default). Mirrors validateTaxCodes in
// functions/utils/taxCodes.js; existing = the codes stored now (or the carry-over).
const normKey = (s) => s.trim().toLowerCase();

function nameProblem(raw, what) {
    if (typeof raw !== 'string' || !raw.trim()) return `Please give ${what} a name.`;
    const name = raw.trim();
    if (name.length > TAX_LIMITS.name) return `"${name.slice(0, 20)}…" is too long. Names can be up to ${TAX_LIMITS.name} characters.`;
    return null;
}
function rateProblem(raw, label) {
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return `Please enter a rate for ${label}.`;
    if (raw < 0 || raw > 100) return `The rate for ${label} must be between 0 and 100.`;
    if (!hasAtMost3Decimals(raw)) return `The rate for ${label} can have at most 3 decimal places.`;
    return null;
}

export function findTaxCodesProblem(codes, existing) {
    const fail = (message, index = null) => ({ message, index });
    if (!Array.isArray(codes)) return fail('Taxes are missing from the save.');
    if (codes.length === 0) return fail('You need at least one tax.');
    if (codes.length > TAX_LIMITS.codes) return fail(`A company can have at most ${TAX_LIMITS.codes} taxes.`);
    const seenNames = new Set();
    for (let i = 0; i < codes.length; i++) {
        const c = codes[i] || {};
        const np = nameProblem(c.name, 'every tax');
        if (np) return fail(np, i);
        const name = c.name.trim();
        if (seenNames.has(normKey(name))) return fail(`You already have a tax named "${name}". Each tax needs a different name.`, i);
        seenNames.add(normKey(name));
        if (!Array.isArray(c.parts) || c.parts.length === 0) return fail(`"${name}" needs at least one part with a name and a rate.`, i);
        if (c.parts.length > TAX_LIMITS.parts) return fail(`"${name}" can have at most ${TAX_LIMITS.parts} parts.`, i);
        const seenParts = new Set();
        for (const p of c.parts) {
            const pp = nameProblem(p && p.name, `every part of "${name}"`);
            if (pp) return fail(pp, i);
            const partName = p.name.trim();
            if (seenParts.has(normKey(partName))) return fail(`"${name}" has two parts named "${partName}". Each part needs a different name.`, i);
            seenParts.add(normKey(partName));
            const rp = rateProblem(p.rate, `"${partName}" in "${name}"`);
            if (rp) return fail(rp, i);
        }
        if (c.isDefault === true && c.stopped === true) {
            return fail(`"${name}" is used on new lines by default, so it can't be stopped. Choose another default first.`, i);
        }
    }
    const kept = new Set(codes.map((c) => c && c.id).filter(Boolean));
    for (const old of existing || []) {
        if (!kept.has(old.id)) return fail(`"${old.name}" can't be deleted. Use "Stop using" instead.`);
    }
    const defaults = codes.filter((c) => c && c.isDefault === true).length;
    if (defaults === 0) return fail('Choose one tax to use on new lines by default.');
    if (defaults > 1) return fail('Only one tax can be the default for new lines.');
    return null;
}

// What the page sends: only the fields the server reads (ids kept for saved codes/parts).
export const toSavePayload = (codes) => codes.map((c) => ({
    ...(c.id ? { id: c.id } : {}),
    name: c.name,
    parts: c.parts.map((p) => ({ ...(p.id ? { id: p.id } : {}), name: p.name, rate: p.rate })),
    isDefault: c.isDefault === true,
    stopped: c.stopped === true,
}));

// The server's out-of-date refusal → reload message instead of a generic error.
export const isStaleTaxCodesError = (err) =>
    !!err && String(err.code || '').endsWith('failed-precondition') && /Someone else changed your taxes/.test(String(err.message || ''));
